import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ChipGroup, JiraLink, KEY_STATUS_LABEL, KeyStatusBadge, PageHeader, Pager, PersonPicker,
  FileProgress, LivenessBadge, LIVENESS_LABEL, NewBadge, PresenceBadge, RawKeySearch, RepoStatusBadge, RiskBadge, SecretsTabs, errMsg, useSecretFilters,
  SampleLink, useSecretsAccess, useUrlFilters, ReasonFields, FpMark, FP_REASON_LABEL,
} from './common';
import { Locations } from './SecretKeyDetail';
import { KeyAnalysisModal } from './KeyAnalysis';

const FILTER_KEYS = ['q', 'status', 'risk', 'type', 'reachability', 'team', 'owner', 'repo_id', 'scope_id',
  'repo_status', 'repo_present', 'revoker', 'jira', 'present', 'in_current_code', 'origin', 'path', 'progress', 'liveness', 'fp_marked_by', 'fp_reviewed', 'fp_reason', 'fp_rule_id',
  'sort', 'dir', 'page', 'limit'];

const SORTS = [
  ['exploit', 'Priority (exploitable)'], ['liveness', 'Liveness'],
  ['risk', 'Risk'], ['occurrences', 'Occurrences'], ['repos', 'Repo count'], ['status', 'Status'],
  ['type', 'Type'], ['updated', 'Last updated'],
];

// ── Bulk actions ──────────────────────────────────────────────────────────────
// One bar for every view that lists keys. It sets key status, revoker and Jira
// card on the selected keys, or on every key matching the view's filters
// (scope 'keys' = GET /secrets/keys filters, 'gone' = GET /secrets/gone).
// repoId adds that repo's sign-off; children add view-specific actions.
export function BulkBar({ keyIds, total, scope = 'keys', filterParams = {}, repoId, scopeId, onDone, onClear, children }) {
  const [revoker, setRevoker] = useState({ email: '', name: '' });
  const [jira, setJira] = useState('');
  const [status, setStatus] = useState('');
  const [note, setNote] = useState('');
  const [fp, setFp] = useState({ reason: '', detail: '' });
  const [confirmedBy, setConfirmedBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const n = keyIds.length;

  const report = (d) => setMsg(d.error_count
    ? `${d.updated} updated, ${d.error_count} refused: ${d.errors[0]?.error}`
    : `${d.updated ?? d.changed} updated`);
  const run = (req) => {
    setBusy(true); setMsg('');
    req.then((r) => { report(r.data); onDone(); })
      .catch((e) => setMsg(errMsg(e))).finally(() => setBusy(false));
  };
  const fields = () => {
    const body = {};
    if (revoker.email) { body.revoker_email = revoker.email; body.revoker_name = revoker.name; }
    if (jira.trim()) body.jira_key = jira.trim();
    if (status) body.status = status;
    if (status === 'false_positive') { body.fp_reason = fp.reason; body.fp_reason_detail = fp.detail; }
    if (note.trim()) body.note = note.trim();
    return body;
  };
  const review = (decision, allMatching) => {
    const count = allMatching ? total : n;
    const what = decision === 'confirm' ? 'Confirm' : 'Reject (re-open)';
    if (!window.confirm(`${what} automatic false-positive marks on ${count} key(s)? Keys not marked automatically are skipped.`)) return;
    const body = { fp_review: decision, note: note.trim() || undefined };
    if (allMatching) {
      const q = { ...filterParams };
      delete q.page; delete q.limit; delete q.sort; delete q.dir;
      run(axios.post('/secrets/keys/bulk', { ...body, all_matching: true, scope }, { params: q }));
    } else {
      run(axios.post('/secrets/keys/bulk', { ...body, key_ids: keyIds }));
    }
  };
  const apply = (allMatching) => {
    const body = fields();
    if (!body.revoker_email && !body.jira_key && !body.status) { setMsg('Pick a status, revoker or Jira card first'); return; }
    if (body.status === 'false_positive' && !body.fp_reason) { setMsg('Pick a reason for the false positive'); return; }
    const count = allMatching ? total : n;
    const what = [body.status && `status → ${KEY_STATUS_LABEL[body.status]}`, body.revoker_email && `revoker → ${body.revoker_name || body.revoker_email}`,
      body.jira_key && `Jira → ${body.jira_key}`].filter(Boolean).join(', ');
    if (!window.confirm(`Set ${what} on ${count} key(s)?` +
      (body.status === 'revoked' ? '\nKeys that are not yet eligible will be refused.' : ''))) return;
    if (allMatching) {
      const q = { ...filterParams };
      delete q.page; delete q.limit; delete q.sort; delete q.dir;
      run(axios.post('/secrets/keys/bulk', { ...body, all_matching: true, scope }, { params: q }));
    } else {
      run(axios.post('/secrets/keys/bulk', { ...body, key_ids: keyIds }));
    }
  };
  const signoff = (st) => {
    if (st === 'ready_to_revoke' && !window.confirm(`Mark ${n} key(s) ready to revoke for this ${scopeId ? 'folder' : 'repo'}?`)) return;
    run(axios.post(`/secrets/repos/${repoId}/signoff`, { status: st, key_ids: keyIds, confirmed_by: confirmedBy, scope_id: scopeId || undefined }));
  };

  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 12, borderColor: n ? 'var(--accent)' : 'var(--border)' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ width: 170 }}>
          <label className="form-label">Key status</label>
          <select className="form-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">No change</option>
            {['open', 'false_positive', 'wont_fix', 'revoked'].map((st) => <option key={st} value={st}>{KEY_STATUS_LABEL[st]}</option>)}
          </select>
        </div>
        {status === 'false_positive' && <ReasonFields compact reason={fp.reason} detail={fp.detail} onChange={setFp} />}
        <div style={{ width: 240 }}>
          <label className="form-label">Revoker</label>
          <PersonPicker email={revoker.email} name={revoker.name} onChange={setRevoker} />
        </div>
        <div style={{ width: 140 }}>
          <label className="form-label">Jira card</label>
          <input className="form-input" value={jira} placeholder="SEC-123" onChange={(e) => setJira(e.target.value)} />
        </div>
        <div style={{ width: 200 }}>
          <label className="form-label">Reason (audit log)</label>
          <input className="form-input" value={note} placeholder="Optional" onChange={(e) => setNote(e.target.value)} />
        </div>
        <button className="btn btn-primary" disabled={busy || !n} onClick={() => apply(false)}>
          Apply to {n || ''} selected
        </button>
        {total > 0 && (
          <button className="btn btn-secondary" disabled={busy} onClick={() => apply(true)}>
            Apply to all {total} matching
          </button>
        )}
        {n > 0 && <button className="btn btn-ghost" onClick={onClear}>Clear selection</button>}
        {busy && <span className="spinner" />}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Automatic false-positive marks:</span>
        <button className="btn btn-secondary" disabled={busy || !n} onClick={() => review('confirm', false)}>Confirm {n || ''} selected</button>
        <button className="btn btn-ghost" disabled={busy || !n} onClick={() => review('reject', false)}>Reject selected (re-open)</button>
        {total > 0 && <button className="btn btn-ghost" disabled={busy} onClick={() => review('confirm', true)}>Confirm all {total} matching</button>}
      </div>
      {repoId && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
          <input className="form-input" style={{ width: 200 }} value={confirmedBy} placeholder="Confirmed by (repo owner)"
            onChange={(e) => setConfirmedBy(e.target.value)} />
          <button className="btn btn-secondary" disabled={busy || !n} onClick={() => signoff('ready_to_revoke')}>
            Mark {n || ''} selected ready for this repo
          </button>
          <button className="btn btn-ghost" disabled={busy || !n} onClick={() => signoff('open')}>Re-open for this repo</button>
        </div>
      )}
      {children}
      {msg && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</div>}
    </div>
  );
}

// ── Table ─────────────────────────────────────────────────────────────────────
export function KeysTable({ rows, jiraBase, selected, setSelected, repoId, onChanged }) {
  const navigate = useNavigate();
  const { can_edit: canEdit } = useSecretsAccess();
  // Repo view only: which key rows have their in-repo places expanded.
  const [open, setOpen] = useState(new Set());
  const [analyze, setAnalyze] = useState(null);
  const toggleOpen = (e, id) => {
    e.stopPropagation();
    const n = new Set(open);
    if (n.has(id)) n.delete(id); else n.add(id);
    setOpen(n);
  };
  const cols = repoId ? 10 : 9;
  const allOn = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  // One sign-off per scope (repo root, or a folder with its own owner).
  const flip = (e, r, sc) => {
    e.stopPropagation();
    const st = (sc ? sc.status : r.repo_status) === 'ready_to_revoke' ? 'open' : 'ready_to_revoke';
    const req = sc ? axios.put(`/secrets/keys/${r.id}/scopes/${sc.scope_id}`, { status: st })
      : axios.put(`/secrets/keys/${r.id}/repos/${repoId}`, { status: st });
    req.then(onChanged).catch((err) => window.alert(errMsg(err)));
  };
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th style={{ width: 28 }}>
              {canEdit && (
                <input type="checkbox" checked={allOn} onChange={() => {
                  const n = new Set(selected);
                  rows.forEach((r) => (allOn ? n.delete(r.id) : n.add(r.id)));
                  setSelected(n);
                }} />
              )}
            </th>
            <th>Key</th>
            <th>Risk</th>
            <th>Type · use (guess)</th>
            <th>Repos</th>
            {repoId && <th>This repo</th>}
            <th>Key status</th>
            <th>Revoker</th>
            <th>Jira</th>
            <th>Liveness</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Fragment key={r.id}>
            <tr style={{ cursor: 'pointer' }} onClick={() => navigate(`/secrets/keys/${r.id}`)}>
              <td onClick={(e) => e.stopPropagation()}>
                {canEdit && <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />}
              </td>
              <td>
                <code style={{ fontSize: 12 }}>{r.key_redacted}</code>
                <SampleLink r={r} />
                <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <PresenceBadge present={r.present_in_any_repo} />
                  {r.origin === 'scan' && <NewBadge since={r.first_seen_at} />}
                </div>
                {r.status !== 'false_positive' && <FileProgress total={r.files_total} left={r.files_left} />}
              </td>
              <td><RiskBadge risk={r.risk_level} type={r.secret_type} />
                {r.reachability && r.reachability !== 'n/a' &&
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{r.reachability}</div>}
              </td>
              <td style={{ maxWidth: 360 }}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.secret_type}</div>
                <div style={{ fontSize: 12 }}>{r.key_use}</div>
              </td>
              <td style={{ fontSize: 12 }}>
                <div>{r.repos_ready}/{r.repo_count} ready · {r.occurrence_count} places</div>
                <div style={{ color: 'var(--text-muted)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={r.slugs}>{(r.slugs || '').split(',').join(', ')}{r.repo_count > 6 ? ' …' : ''}</div>
              </td>
              {repoId && (
                <td onClick={(e) => e.stopPropagation()}>
                  {(r.repo_scopes || []).length > 1 ? (
                    <div style={{ display: 'grid', gap: 4 }}>
                      {r.repo_scopes.map((sc) => (
                        <div key={sc.scope_id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                          <span style={{ color: 'var(--text-muted)', minWidth: 90, wordBreak: 'break-all' }}
                            title={sc.owner_name ? `Owner ${sc.owner_name}${sc.team ? ` · ${sc.team}` : ''}` : ''}>
                            {sc.folder_path || '(repo root)'}
                          </span>
                          {canEdit ? (
                            <button className="btn btn-ghost" style={{ padding: 0 }} onClick={(e) => flip(e, r, sc)}
                              title="Click to toggle this folder's sign-off">
                              <RepoStatusBadge status={sc.status} />
                            </button>
                          ) : <RepoStatusBadge status={sc.status} />}
                          <PresenceBadge present={sc.present} reason={sc.removed_reason} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {r.repo_status && !canEdit && <RepoStatusBadge status={r.repo_status} />}
                      {r.repo_status && canEdit ? (
                        <button className="btn btn-ghost" style={{ padding: 0 }} onClick={(e) => flip(e, r, r.repo_scopes?.[0])}
                          title="Click to toggle this sign-off">
                          <RepoStatusBadge status={r.repo_status} />
                        </button>
                      ) : (!r.repo_status && '—')}
                      {r.repo_scopes?.[0]?.folder_path && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>folder {r.repo_scopes[0].folder_path}</div>
                      )}
                      <div style={{ marginTop: 4 }}><PresenceBadge present={r.repo_present} reason={r.repo_removed_reason} /></div>
                    </>
                  )}
                  <button className="btn btn-ghost" style={{ padding: '2px 0', marginTop: 4, fontSize: 12 }}
                    onClick={(e) => toggleOpen(e, r.id)} aria-expanded={open.has(r.id)}>
                    {open.has(r.id) ? '▾' : '▸'} {r.repo_head_locations ?? 0} in code · {r.repo_locations ?? 0} total
                  </button>
                </td>
              )}
              <td><KeyStatusBadge status={r.status} /><FpMark r={r} /></td>
              <td style={{ fontSize: 12 }}>{r.revoker_name || r.revoker_email || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
              <td onClick={(e) => e.stopPropagation()}><JiraLink jiraKey={r.jira_key} base={jiraBase} /></td>
              <td onClick={(e) => e.stopPropagation()}>
                <LivenessBadge liveness={r.liveness} onClick={() => setAnalyze(r)}
                  title={r.liveness && r.liveness !== 'unknown' ? 'View deep analysis' : 'Run a deep analysis'} />
              </td>
            </tr>
            {repoId && open.has(r.id) && (
              <tr style={{ background: 'var(--bg-base)' }}>
                <td colSpan={cols} style={{ padding: '8px 16px 12px 44px' }}>
                  <Locations keyId={r.id} fixedRepoId={repoId} embedded
                    defaultScope={r.repo_head_locations ? 'head' : 'all'} />
                </td>
              </tr>
            )}
            </Fragment>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={cols} style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>
              No keys match these filters.
            </td></tr>
          )}
        </tbody>
      </table>
      {analyze && (
        <KeyAnalysisModal keyId={analyze.id} redacted={analyze.key_redacted}
          onClose={() => setAnalyze(null)}
          onChanged={(a) => { if (a && a.status !== 'analyzing' && a.status !== 'probing') onChanged?.(); }} />
      )}
    </div>
  );
}

// ── Filter bar ────────────────────────────────────────────────────────────────
export function KeyFilters({ get, set, filters, hideRepo, showRepoStatus, showProgress = true, showGoneReason = false, scopes = null, sorts = SORTS, defaultSort = 'risk', defaultDir = 'asc' }) {
  const sel = (key, label, options, width = 170) => (
    <div style={{ width }}>
      <label className="form-label">{label}</label>
      <select className="form-input" value={get(key)} onChange={(e) => set({ [key]: e.target.value })}>
        <option value="">Any</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
  const f = filters || {};
  // One adaptive "Code status" control. When narrowed to a team / owner / repo,
  // "in code" means in those repos (repo_present); otherwise it means anywhere
  // (present). Only the scope-appropriate param is ever set, so the two never
  // clash.
  const scoped = !!(get('team') || get('owner') || get('repo_id'));
  const codeVal = scoped ? get('repo_present') : get('present');
  const setCode = (v) => {
    const patch = scoped ? { repo_present: v, present: '' } : { present: v, repo_present: '' };
    if (v === 'yes' && !get('status')) patch.status = 'open,eligible,revoked';
    set(patch);
  };
  useEffect(() => {
    // When the scope changes, move any presence filter to the matching param.
    if (scoped && get('present')) set({ repo_present: get('repo_present') || get('present'), present: '' });
    else if (!scoped && get('repo_present')) set({ present: get('present') || get('repo_present'), repo_present: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped]);
  return (
    <div className="card" style={{ padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <label className="form-label">Key status</label>
          <ChipGroup options={['open', 'eligible', 'revoked', 'false_positive', 'wont_fix']} labels={KEY_STATUS_LABEL}
            value={get('status')} onChange={(v) => set({ status: v })} />
        </div>
        <div>
          <label className="form-label">Risk</label>
          <ChipGroup options={['Critical', 'High', 'Medium', 'Low', 'none']} labels={{ none: 'AWS runbook' }}
            value={get('risk')} onChange={(v) => set({ risk: v })} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ width: 220 }}>
          <label className="form-label">Text</label>
          <input className="form-input" defaultValue={get('q')} placeholder="Redacted key, use, digest, Jira"
            onKeyDown={(e) => { if (e.key === 'Enter') set({ q: e.target.value.trim() }); }}
            onBlur={(e) => { if (e.target.value.trim() !== get('q')) set({ q: e.target.value.trim() }); }} />
        </div>
        {sel('team', 'Team', [...(f.teams || []).map((t) => [t, t]), ['Unknown', 'Unknown (owner not in directory)']])}
        {sel('owner', 'Repo owner', (f.owners || []).map((o) => [o.email, `${o.name || o.email} (${o.repos})`]), 220)}
        {!hideRepo && sel('repo_id', 'Repo', (f.repos || []).map((r) => [String(r.id), r.slug]), 200)}
        {sel('type', 'Type', (f.types || []).map((t) => [t, t]))}
        {sel('reachability', 'Reachability', (f.reachability || []).map((t) => [t, t]), 130)}
        {sel('revoker', 'Revoker', [['none', 'Not assigned'], ...(f.revokers || []).map((o) => [o.email, `${o.name || o.email} (${o.n})`])], 200)}
        {sel('jira', 'Jira card', [['yes', 'Has a card'], ['no', 'No card']], 120)}
        {sel('fp_marked_by', 'FP set by', [['person', 'A person'], ['auto', 'A rule']], 130)}
        {sel('fp_reviewed', 'Auto review', [['no', 'Awaiting review'], ['yes', 'Reviewed']], 140)}
        {sel('fp_reason', 'FP reason', Object.entries(FP_REASON_LABEL), 200)}
        {sel('fp_rule_id', 'Rule', (f.fp_rules || []).map((r) => [String(r.id), `#${r.id} ${r.name}`]), 200)}
        <div style={{ width: 180 }}>
          <label className="form-label">Code status</label>
          <select className="form-input" value={codeVal} onChange={(e) => setCode(e.target.value)}>
            <option value="">Any</option>
            <option value="yes">{scoped ? 'Still in code here' : 'Still in code'}</option>
            <option value="no">{scoped ? 'Gone from here' : 'Gone from code'}</option>
          </select>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
            {scoped ? 'in the repos matching your filters' : 'across all repositories'}
          </div>
        </div>
        {showProgress && sel('progress', 'Cleanup', [['partial', 'Partly cleaned']], 140)}
        {showGoneReason && sel('gone_reason', 'Why gone', [['scan', 'Removed from code'], ['archived', 'Repo archived']], 160)}
        {sel('origin', 'Found', [['baseline', 'In the 23 Sep scan'], ['scan', 'New since 23 Sep']], 160)}
        {sel('liveness', 'Liveness', [['active', LIVENESS_LABEL.active], ['maybe_inactive', LIVENESS_LABEL.maybe_inactive], ['awaiting_probe', LIVENESS_LABEL.awaiting_probe], ['unverified', LIVENESS_LABEL.unverified], ['unreachable', LIVENESS_LABEL.unreachable], ['unknown', LIVENESS_LABEL.unknown]], 160)}
        <div style={{ width: 220 }}>
          <label className="form-label">Path starts with</label>
          <input className="form-input" defaultValue={get('path')} placeholder="e.g. getafix/trunk/ansible"
            onKeyDown={(e) => { if (e.key === 'Enter') set({ path: e.target.value.trim() }); }}
            onBlur={(e) => { if (e.target.value.trim() !== get('path')) set({ path: e.target.value.trim() }); }} />
        </div>
        {scopes && scopes.length > 1 && sel('scope_id', 'Folder', scopes.map((sc) => [String(sc.id), sc.folder_path || '(repo root)']), 200)}
        {(get('repo_id') || hideRepo || showRepoStatus) && sel('repo_status', 'Repo sign-off', [['open', 'Open'], ['ready_to_revoke', 'Ready to revoke']], 150)}
        <div style={{ width: 150 }}>
          <label className="form-label">Sort</label>
          <select className="form-input" value={get('sort') || defaultSort} onChange={(e) => set({ sort: e.target.value })}>
            {sorts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <button className="btn btn-ghost" onClick={() => set({ dir: (get('dir') || defaultDir) === 'desc' ? 'asc' : 'desc' })}>
          {(get('dir') || defaultDir) === 'desc' ? '↓ Desc' : '↑ Asc'}
        </button>
        <button className="btn btn-ghost" onClick={() => set(Object.fromEntries(FILTER_KEYS.filter((k) => !hideRepo || k !== 'repo_id').map((k) => [k, ''])))}>
          Reset
        </button>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export function useKeyList(get, params, extra = {}) {
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const query = params.toString() + JSON.stringify(extra);
  const load = useCallback(() => {
    setLoading(true); setError('');
    const p = {};
    FILTER_KEYS.forEach((k) => { if (get(k)) p[k] = get(k); });
    Object.assign(p, extra);
    axios.get('/secrets/keys', { params: p })
      .then((r) => setData(r.data)).catch((e) => setError(errMsg(e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error, load };
}

export default function SecretKeysList() {
  const { params, get, set } = useUrlFilters();
  const { can_edit: canEdit } = useSecretsAccess();
  const filters = useSecretFilters();
  const navigate = useNavigate();
  const { data, loading, error, load } = useKeyList(get, params);
  const [selected, setSelected] = useState(new Set());
  const [hits, setHits] = useState(null);
  const repoId = get('repo_id');

  useEffect(() => { setSelected(new Set()); }, [params]);

  const onHits = (rows) => {
    if (rows.length === 1) navigate(`/secrets/keys/${rows[0].id}`);
    else setHits(rows);
  };

  return (
    <div>
      <PageHeader title="Secrets rotation · Keys" />
      <div className="page-body">
        <SecretsTabs />
        <div className="card" style={{ padding: '12px 16px', marginBottom: 12 }}>
          <RawKeySearch onResults={onHits} />
          {hits && hits.length > 1 && (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              Several stored keys match (the same value saved with different line endings):{' '}
              {hits.map((h) => <Link key={h.id} to={`/secrets/keys/${h.id}`} style={{ marginRight: 10 }}>{h.key_redacted}</Link>)}
            </div>
          )}
        </div>
        <KeyFilters get={get} set={set} filters={filters} />
        {canEdit && (
          <BulkBar keyIds={[...selected]} total={data.total} filterParams={Object.fromEntries(params)}
            repoId={repoId && /^\d+$/.test(repoId) ? repoId : null}
            onDone={() => { setSelected(new Set()); load(); }} onClear={() => setSelected(new Set())} />
        )}
        {error && <div className="error-msg">{error}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <span style={{ color: 'var(--text-secondary)' }}>{data.total} keys</span>
          {loading && <span className="spinner" />}
          <span style={{ flex: 1 }} />
          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Per page{' '}
            <select value={get('limit') || '50'} onChange={(e) => set({ limit: e.target.value })}>
              {['50', '100', '200', '500'].map((n) => <option key={n}>{n}</option>)}
            </select>
          </label>
        </div>
        <KeysTable rows={data.data} jiraBase={filters?.jira_base_url} selected={selected} setSelected={setSelected}
          repoId={repoId && /^\d+$/.test(repoId) ? repoId : null} onChanged={load} />
        <Pager page={data.page} limit={data.limit} total={data.total} onPage={(p) => set({ page: p }, { keepPage: true })} />
      </div>
    </div>
  );
}
